import { produce } from "immer";
import { ActionType, createReducer } from "typesafe-actions";

import { SETTINGS_LOAD_ERROR } from "../constants";
import { SettingsStateType } from "../interfaces";
import * as actions from "./actions";

type Action = ActionType<typeof actions>;

export const initialState: SettingsStateType = {
  sections: [],
  saving: false,
  loadError: null,
};

const reducer = createReducer<SettingsStateType, Action>(initialState)
  .handleAction(actions.fetchSettings.request, (state) =>
    produce(state, (nextState) => {
      nextState.loadError = null;
    }),
  )
  .handleAction(actions.fetchSettings.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.sections = action.payload;
      nextState.loadError = null;
    }),
  )
  .handleAction(actions.fetchSettings.failure, (state) =>
    produce(state, (nextState) => {
      nextState.loadError = SETTINGS_LOAD_ERROR;
    }),
  )
  .handleAction(actions.saveSettings.request, (state) =>
    produce(state, (nextState) => {
      nextState.saving = true;
    }),
  )
  // Writes each saved key's new value into `sections` -- this, not a form reset, is what makes a
  // just-saved key read as clean again: SettingsSection's dirty check compares the draft against
  // `sections`, so once the store agrees with what was submitted, that key stops being dirty on its
  // own. A key the user keeps editing during the save stays dirty even after this runs, since its
  // draft (now further ahead) no longer matches the value just written here either.
  .handleAction(actions.saveSettings.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.saving = false;
      nextState.sections.forEach((section) => {
        section.settings.forEach((setting) => {
          const saved = action.payload.savedSettings.find((s) => s.key === setting.key);
          if (saved) {
            setting.value = saved.value;
          }
        });
      });
    }),
  )
  .handleAction(actions.saveSettings.failure, (state) =>
    produce(state, (nextState) => {
      nextState.saving = false;
    }),
  );

export { reducer as SettingsReducer };
